import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { acceptInvitation, getInvitationDetailsByToken } from '../../utils/tripPlanUtils';
import { useAuth } from '../../hooks/useAuth';

const AcceptInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [invitationData, setInvitationData] = useState(null);
  const [tripPlan, setTripPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (token && !authLoading) { // Wait for auth to load
      fetchInvitationData();
    }
  }, [token, authLoading]);

  const fetchInvitationData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const result = await getInvitationDetailsByToken(token);
      
      if (result.success) {
        setTripPlan(result.tripPlan);
        setInvitationData(result.invitation);
        
        // Debug logging
        console.log('=== DEBUG ACCEPT INVITATION ===');
        console.log('User:', user);
        console.log('User email:', user?.email);
        console.log('Trip Plan ID:', result.tripPlan.id);
        console.log('Trip Plan Name:', result.tripPlan.tripName);
        console.log('Trip Plan Creator:', result.tripPlan.createdBy);
        console.log('Invitation email:', result.invitation.email);
        console.log('Invitation status:', result.invitation.status);
        console.log('Invitation token:', result.invitation.token);
        console.log('Expires at:', result.invitation.expiresAt);
        console.log('Full Trip Plan:', result.tripPlan);
        console.log('Full Invitation:', result.invitation);
        console.log('===============================');
        
        if (new Date(result.invitation.expiresAt) < new Date()) { // expiresAt is now ISO string
          setError('คำเชิญหมดอายุแล้ว');
        } else if (result.invitation.status !== 'pending') {
          setError('คำเชิญนี้ถูกใช้แล้ว');
        } else if (user && result.invitation.email !== user.email) {
          setError('คำเชิญนี้ไม่ใช่อีเมลของคุณ');
        }
      } else {
        setError(result.error || 'ไม่พบคำเชิญหรือคำเชิญไม่ถูกต้อง');
      }
    } catch (err) {
      console.error('Error fetching invitation data:', err);
      setError('เกิดข้อผิดพลาดในการดึงข้อมูลคำเชิญ');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!user) {
      navigate('/login'); // Redirect to login if not authenticated
      return;
    }
    if (invitationData.email !== user.email) {
      setError('คุณต้องเข้าสู่ระบบด้วยอีเมลที่ได้รับคำเชิญ');
      return;
    }

    try {
      setProcessing(true);
      setError('');
      
      const result = await acceptInvitation(tripPlan.id, invitationData.email, user.uid);
      
      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate(`/trip-plans`); // Redirect to trip plans page
        }, 3000);
      } else {
        setError(result.error || 'ไม่สามารถยอมรับคำเชิญได้');
      }
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError('เกิดข้อผิดพลาดในการยอมรับคำเชิญ');
    } finally {
      setProcessing(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-lg text-gray-600">กำลังโหลด...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-3xl">❌</span>
          </div>
          <h2 className="text-2xl font-bold text-red-700 mb-4">เกิดข้อผิดพลาด</h2>
          <p className="text-gray-700 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            กลับหน้าหลัก
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-green-600 text-3xl">✅</span>
          </div>
          <h2 className="text-2xl font-bold text-green-700 mb-4">ยอมรับคำเชิญสำเร็จ!</h2>
          <p className="text-gray-700 mb-6">คุณได้เข้าร่วมทริป "{tripPlan?.tripName}" ในฐานะผู้ช่วยแล้ว</p>
          <button
            onClick={() => navigate('/trip-plans')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            ไปที่หน้า Trip Plans
          </button>
        </div>
      </div>
    );
  }

  if (!invitationData || !tripPlan) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-lg text-gray-600">ไม่พบข้อมูลคำเชิญ</div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6 text-center">
          คำเชิญเข้าร่วมทริป
        </h1>

        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-blue-600 text-2xl">📧</span>
          </div>
          <div>
            <p className="text-gray-600 text-sm">คุณได้รับคำเชิญจาก:</p>
            <p className="text-lg font-semibold text-gray-800">{tripPlan.ownerEmail}</p>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-6 mt-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            ทริป: {tripPlan.tripName}
          </h2>
          <p className="text-gray-700 mb-4">{tripPlan.tripDescription}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-600 text-sm mb-6">
            {tripPlan.tripLocation && (
              <div className="flex items-center space-x-2">
                <span className="text-gray-500">📅</span>
                <span>{tripPlan.tripLocation}</span>
              </div>
            )}
            {tripPlan.tripTheme && (
              <div className="flex items-center space-x-2">
                <span className="text-gray-500">👥</span>
                <span>{tripPlan.tripTheme}</span>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <span className="text-gray-500">⏰</span>
              <span>สร้างเมื่อ {new Date(tripPlan.createdAt).toLocaleDateString('th-TH')}</span>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-6 mt-6">
          <h3 className="text-xl font-bold text-gray-900 mb-3">
            คุณจะได้รับสิทธิ์:
          </h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-center space-x-2">
              <span className="text-green-500">✅</span>
              <span>ช่วยทำ checklist ทริป</span>
            </li>
            <li className="flex items-center space-x-2">
              <span className="text-green-500">✅</span>
              <span>จองคิวน้องแทนผู้จัดทริป</span>
            </li>
            <li className="flex items-center space-x-2">
              <span className="text-green-500">✅</span>
              <span>ดูข้อมูลทริปทั้งหมด</span>
            </li>
          </ul>
        </div>

        <div className="mt-8 text-center">
          <button
            onClick={handleAcceptInvitation}
            disabled={processing || !user || invitationData.email !== user.email}
            className={`px-8 py-4 rounded-lg text-lg font-semibold transition-colors ${
              processing || !user || invitationData.email !== user.email
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {processing ? 'กำลังดำเนินการ...' : 'ยอมรับคำเชิญ'}
          </button>
          {!user && (
            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-500 text-sm">
                กรุณาเข้าสู่ระบบด้วยอีเมลที่ได้รับคำเชิญเพื่อยอมรับ
              </p>
              {invitationData && (
                <p className="text-red-600 text-sm mt-1">
                  <strong>อีเมลที่ได้รับคำเชิญ:</strong> {invitationData.email}
                </p>
              )}
            </div>
          )}
          {user && invitationData && user.email !== invitationData.email && (
            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800 text-sm">
                <strong>อีเมลที่ได้รับคำเชิญ:</strong> {invitationData.email}
              </p>
              <p className="text-yellow-800 text-sm">
                <strong>อีเมลที่ login:</strong> {user.email}
              </p>
              <p className="text-yellow-800 text-sm mt-1">
                กรุณาออกจากระบบและ login ด้วยอีเมลที่ถูกต้อง
              </p>
            </div>
          )}
          {user && invitationData.email !== user.email && (
            <p className="text-red-500 text-sm mt-2">
              คุณต้องเข้าสู่ระบบด้วยอีเมล {invitationData.email} เพื่อยอมรับคำเชิญนี้
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation;
